import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Globe, ArrowLeft, Send, Target, AlertTriangle, 
  CheckCircle2, Sparkles, MessageCircle, Zap, Lock
} from 'lucide-react';

function VCInterview() {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth.user;
  const token = auth.token;
  
  const [stage, setStage] = useState('start');
  const [businessIdea, setBusinessIdea] = useState('');
  const [industry, setIndustry] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [finalAssessment, setFinalAssessment] = useState(null);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  async function handleStartInterview() {
    if (!businessIdea.trim()) {
      toast.error('Please describe your business idea');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(API + '/ai/vc/start', {
        business_idea: businessIdea,
        industry: industry || null
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      setSessionId(response.data.session_id);
      setMessages([{
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date().toISOString()
      }]);
      setStage('interview');
      setMessageCount(0);
    } catch (error) {
      console.error('Failed to start interview:', error);
      toast.error('Failed to start interview. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSendMessage() {
    if (!inputValue.trim() || loading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    
    setLoading(true);
    try {
      const response = await axios.post(API + '/ai/vc/chat', {
        session_id: sessionId,
        message: userMessage
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.message,
        timestamp: new Date().toISOString()
      }]);
      
      setMessageCount(response.data.message_count);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleEndInterview() {
    if (!sessionId || loading) return;
    
    setLoading(true);
    try {
      const response = await axios.post(API + '/ai/vc/end', {
        session_id: sessionId,
        message: 'end'
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      setFinalAssessment(response.data);
      setStage('complete');
    } catch (error) {
      console.error('Failed to end interview:', error);
      toast.error('Failed to get final assessment. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="vc-interview-page">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#DC143C] opacity-5 blur-[150px] rounded-full" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#8B0000] opacity-5 blur-[150px] rounded-full" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-header">
        <div className="container-wide py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-[#71717A] hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl gradient-red flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold tracking-tight hidden sm:block">
                  <span className="text-white">Visa</span>
                  <span className="text-gradient-red">Founders</span>
                </span>
              </Link>
            </div>
            <Badge className="badge-red">
              <Sparkles className="w-3 h-3 mr-1" />
              GPT-5.2 Powered
            </Badge>
          </div>
        </div>
      </nav>

      <main className="container-wide pt-28 pb-12 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            AI <span className="text-gradient-red">Venture Capital</span> Interview
          </h1>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Validate your business idea with a harsh VC who will challenge every assumption
          </p>
        </motion.div>

        <AnimatePresence mode="wait">
          {stage === 'start' && (
            <motion.div
              key="start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <StartScreen 
                businessIdea={businessIdea}
                setBusinessIdea={setBusinessIdea}
                industry={industry}
                setIndustry={setIndustry}
                onStart={handleStartInterview}
                loading={loading}
              />
            </motion.div>
          )}
          
          {stage === 'interview' && (
            <motion.div
              key="interview"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <InterviewScreen
                messages={messages}
                messageCount={messageCount}
                inputValue={inputValue}
                setInputValue={setInputValue}
                onSend={handleSendMessage}
                onEnd={handleEndInterview}
                onKeyPress={handleKeyPress}
                loading={loading}
                messagesEndRef={messagesEndRef}
              />
            </motion.div>
          )}
          
          {stage === 'complete' && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <CompleteScreen
                finalAssessment={finalAssessment}
                onReset={() => {
                  setStage('start');
                  setBusinessIdea('');
                  setIndustry('');
                  setSessionId(null);
                  setMessages([]);
                  setFinalAssessment(null);
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Support Button */}
      <button className="support-btn" data-testid="support-btn">
        <MessageCircle className="w-6 h-6 text-white" />
      </button>
    </div>
  );
}

function StartScreen({ businessIdea, setBusinessIdea, industry, setIndustry, onStart, loading }) {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="card-glass rounded-2xl p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-2xl gradient-red flex items-center justify-center mx-auto mb-6 glow-red">
            <Target className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">VC Discovery Engine</h2>
          <p className="text-[#A1A1AA]">
            Face a harsh Silicon Valley VC who will challenge your assumptions
          </p>
        </div>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2 text-[#A1A1AA]">
              Describe your business idea *
            </label>
            <Textarea
              value={businessIdea}
              onChange={(e) => setBusinessIdea(e.target.value)}
              placeholder="E.g., An AI-powered platform that helps immigrants navigate visa applications..."
              className="input-dark min-h-32"
              data-testid="vc-business-idea-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2 text-[#A1A1AA]">
              Industry (optional)
            </label>
            <Input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="E.g., LegalTech, FinTech, HealthTech..."
              className="input-dark"
              data-testid="vc-industry-input"
            />
          </div>
          
          <div className="card-dark rounded-xl p-4 border border-yellow-500/20 bg-yellow-500/5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-yellow-500 mb-1">Expect tough questions!</p>
                <p className="text-[#A1A1AA]">This VC has seen 1000s of pitches and will challenge every assumption. The harsh feedback now saves you from rejection later.</p>
              </div>
            </div>
          </div>
          
          <Button
            onClick={onStart}
            disabled={loading || !businessIdea.trim()}
            className="w-full btn-primary py-6 text-lg glow-red"
            data-testid="vc-start-interview-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Starting...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Start VC Interview
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function InterviewScreen({ messages, messageCount, inputValue, setInputValue, onSend, onEnd, onKeyPress, loading, messagesEndRef }) {
  const messageElements = [];
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const isUser = msg.role === 'user';
    messageElements.push(
      <motion.div 
        key={i}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={'flex ' + (isUser ? 'justify-end' : 'justify-start')}
        data-testid={'vc-message-' + i}
      >
        <div className={isUser ? 'chat-message-user' : 'chat-message-ai'}>
          <p className="whitespace-pre-wrap">{msg.content}</p>
        </div>
      </motion.div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="chat-container">
        {/* Header */}
        <div className="gradient-red px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold">Harsh VC Interview</h3>
                <p className="text-sm text-white/70">Messages: {messageCount}/10+</p>
              </div>
            </div>
            {messageCount >= 8 && (
              <Button
                onClick={onEnd}
                disabled={loading}
                className="btn-secondary bg-white/10 border-white/20 text-white hover:bg-white/20"
                data-testid="vc-end-interview-btn"
              >
                Get Final Assessment
              </Button>
            )}
          </div>
        </div>
        
        {/* Messages */}
        <div className="h-[500px] overflow-y-auto p-6 space-y-4 bg-[#0A0A0A]" data-testid="vc-messages-container">
          {messageElements}
          {loading && (
            <div className="flex justify-start">
              <div className="chat-message-ai">
                <div className="typing-indicator">
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                  <div className="typing-dot" />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div className="chat-input-container p-4">
          <div className="flex gap-3">
            <Textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={onKeyPress}
              placeholder="Type your response..."
              className="chat-input flex-1 min-h-12 max-h-32 resize-none"
              disabled={loading}
              data-testid="vc-chat-input"
            />
            <Button
              onClick={onSend}
              disabled={loading || !inputValue.trim()}
              className="btn-primary px-6"
              data-testid="vc-send-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompleteScreen({ finalAssessment, onReset }) {
  if (!finalAssessment) return null;
  
  return (
    <div className="max-w-4xl mx-auto">
      <div className="card-glass rounded-2xl overflow-hidden">
        <div className="gradient-red px-8 py-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">VC Assessment Complete</h2>
              <p className="text-white/70">Business: {finalAssessment.business_idea}</p>
            </div>
          </div>
        </div>
        
        <div className="p-8">
          <div className="prose prose-invert max-w-none" data-testid="vc-final-assessment">
            <div className="whitespace-pre-wrap text-[#A1A1AA] leading-relaxed">
              {finalAssessment.final_assessment}
            </div>
          </div>
          
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Button
              onClick={onReset}
              className="btn-secondary flex-1"
              data-testid="vc-new-interview-btn"
            >
              Start New Interview
            </Button>
            <Link to="/tools/business-plan" className="flex-1">
              <Button className="w-full btn-primary glow-red" data-testid="vc-go-to-plan-btn">
                <Lock className="w-4 h-4 mr-2" />
                Lock Idea & Build Plan
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VCInterview;
